import {Autocomplete, Button, IconButton, Stack, TextField} from "@mui/material";
import {useEffect, useState} from "react";
import {FiTrash2} from "react-icons/fi";
import {scopesColumns} from "../../constants/dataGridColumns";
import ListView from "../ListView/ListView";
import useApiRequest from "../../hooks/useApiRequest";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../ui/ConfirmDialog";

const ClientScopesGrid = ({clientId}) => {
  const [isSettingNewScope, setIsSettingNewScope] = useState(false);
  const [selectedScopeId, setSelectedScopeId] = useState(null);
  const [scopesSelect, setScopesSelect] = useState([]);

  const {data, loading, list, remove, create} = useApiRequest({
    baseUrl: `/clients/${clientId}/scopes`,
    resource: "clients",
    field: "scopes"
  });

  const {
    data: dataScopes,
    loading: loadingScopes,
    list: listScopes
  } = useApiRequest({baseUrl: `/scopes`, resource: "scopes"});

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    try {
      await remove(id);
      await list();
      await listScopes();
    } catch (error) {
      console.error("Error al eliminar el scope:", error);
    }
  });

  useEffect(() => {
    (async () => {
      try {
        await list();
        await listScopes();
      } catch (error) {
        console.error("Error al cargar los scopes:", error);
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (scopesSelect?.length === 0 && dataScopes && data) {
      const clientScopesIds = data?.length > 0 ? data.map((scope) => scope.id) : [];
      const scopesFiltered = dataScopes.filter((scope) => !clientScopesIds.includes(scope.id));
      const transformedList = scopesFiltered.map((scope) => ({
        id: scope.id,
        label: scope.name
      }));
      setScopesSelect(transformedList);
    }
  }, [dataScopes, data, scopesSelect]);

  const handleAccept = async () => {
    try {
      if (!selectedScopeId) return;
      const result = await create({scope_id: selectedScopeId});

      if (result.success) {
        setScopesSelect((prev) => prev.filter((scope) => scope.id !== selectedScopeId));
      }

      await listScopes();
      await list();
    } catch (error) {
      console.error("Error al recargar los scopes:", error);
    }
  };

  const handleScopeSelect = (event, value) => {
    setSelectedScopeId(value?.id || null);
  };

  const handleOpenDeleteDialog = (id) => {
    const found = Array.isArray(data) ? data.find((scope) => scope.id === id) : null;
    openDialog(id, found?.name || "");
  };

  const columns = [
    ...scopesColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="delete"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDeleteDialog(params.row.id);
          }}
        >
          <FiTrash2 />
        </IconButton>
      )
    }
  ];

  return (
    <>
      <ListView
        viewTitle={"Scopes"}
        columns={columns}
        data={data || []}
        loading={loading || loadingScopes}
        topRightButtons={
          <Stack direction="row" spacing={2}>
            {isSettingNewScope && (
              <Stack direction="row" spacing={2}>
                <Autocomplete
                  disablePortal
                  options={scopesSelect}
                  sx={{width: 300}}
                  onChange={handleScopeSelect}
                  renderInput={(params) => <TextField {...params} label="Scope" />}
                />
                <Button variant="contained" onClick={handleAccept}>
                  Aceptar
                </Button>
              </Stack>
            )}
            <Button
              variant="contained"
              color={isSettingNewScope ? "error" : "primary"}
              onClick={() => {
                setIsSettingNewScope(!isSettingNewScope);
              }}
            >
              {isSettingNewScope ? "Cancelar" : "Asignar scope"}
            </Button>
          </Stack>
        }
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="scope"
        entityLabel={itemName}
      />
    </>
  );
};

export default ClientScopesGrid;
